import { Button } from "@/components/ui/button";
import { Phone } from "lucide-react";
import { Link } from "react-router-dom";
import { trackEvent } from "@/lib/analytics";
import heroBg from "@/assets/hero-bg.webp";
import heroBgMobile from "@/assets/hero-bg-mobile.webp";
import heroBgDesktop from "@/assets/hero-bg-desktop.webp";
import heroBgDesktopMobile from "@/assets/hero-bg-desktop-mobile.webp";
import NhsPill from "@/components/landing/NhsPill";
import PillTrustpilotWidget from "@/components/pill-page/PillTrustpilotWidget";

const PillHero = () => {
  return (
    <section className="relative overflow-hidden bg-hero-bg" aria-label="Get the contraceptive pill online">
      <div className="relative z-10 px-6 pt-4 pb-6 lg:pt-8 lg:pb-12 lg:px-16 xl:px-24">
        <div className="max-w-xl lg:max-w-7xl mx-auto">
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="mb-2 text-xs text-muted-foreground">
            <a href="/" className="hover:text-primary transition-colors">Home</a>
            <span className="mx-1.5">›</span>
            <span className="text-foreground font-medium">Get the Pill Online</span>
          </nav>

          <div className="lg:grid lg:grid-cols-[1fr_1fr] lg:gap-12 xl:gap-20 lg:items-center">
            {/* Left column */}
            <div>
              <NhsPill />

              <h1 className="relative z-10 text-[1.65rem] sm:text-3xl lg:text-[2.6rem] xl:text-[3rem] font-bold leading-[1.08] tracking-tight mt-3 mb-3 animate-fade-up">
                <span className="bg-gradient-to-r from-foreground to-[hsl(260,15%,38%)] bg-clip-text text-transparent">Get the Contraceptive Pill Online:</span>{" "}
                <span className="text-primary">100% Free</span>
              </h1>

              {/* Mobile: image right after headline */}
              <div className="relative w-[calc(100%+3rem)] -mx-6 -mt-8 -mb-12 lg:hidden animate-fade-up z-0" style={{ animationDelay: "0.05s" }}>
                <div className="absolute top-0 left-0 right-0 h-8 bg-gradient-to-b from-hero-bg to-transparent z-10 pointer-events-none" />
                <picture>
                  <source media="(min-width: 750px)" srcSet={heroBg} />
                  <img src={heroBgMobile} alt="Contraception Direct package" className="w-full h-auto object-contain" width={750} height={500} loading="eager" />
                </picture>
                <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-hero-bg to-transparent z-10 pointer-events-none" />
              </div>

              {/* Subtitle */}
              <p className="relative z-10 text-[15px] lg:text-base text-muted-foreground leading-relaxed mb-4 animate-fade-up max-w-xl" style={{ animationDelay: "0.1s" }}>
                A short phone consultation with a pharmacist (about 5 minutes for a repeat supply), then your pill arrives in discreet packaging within 1 to 3 days. Since February 2024, pharmacies in England have carried out more than 1.4 million{" "}
                <a href="https://www.england.nhs.uk/2026/07/hundreds-thousands-women-access-free-morning-after-pill-nhs-pharmacies-first-5-months/" target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-2">
                  NHS Pharmacy Contraception Service
                </a>{" "}
                consultations for people starting or continuing the pill.
              </p>

              {/* Trustpilot */}
              <div className="relative z-10 animate-fade-up mt-2" style={{ animationDelay: "0.15s" }}>
                <PillTrustpilotWidget />
              </div>

              {/* CTA */}
              <div className="relative z-10 animate-fade-up flex justify-center lg:justify-start mt-4" style={{ animationDelay: "0.2s" }}>
                <Link to="/book/" onClick={() => trackEvent("cta_click", { location: "get-the-pill-online", cta: "hero" })}>
                  <Button variant="cta" size="pillLg" className="group h-auto flex-row items-center gap-2 text-lg px-5 py-2 lg:px-8 lg:py-3 transition-all duration-300">
                    <Phone className="w-7 h-7" />
                    <div className="flex flex-col">
                      <span>Start Now</span>
                      <span className="text-xs opacity-80 font-semibold">No account needed</span>
                    </div>
                  </Button>
                </Link>
              </div>

            </div>

            {/* Right column: desktop hero image */}
            <div className="hidden lg:flex lg:items-center lg:justify-center">
              <div className="relative w-full animate-fade-up" style={{ animationDelay: "0.1s" }}>
                <picture>
                  <source media="(min-width: 1280px)" srcSet={heroBgDesktop} />
                  <img
                    src={heroBgDesktopMobile}
                    alt="Contraception Direct package"
                    className="w-full h-auto object-contain mx-auto max-w-lg xl:max-w-xl"
                    width={750}
                    height={500}
                    loading="eager"
                  />
                </picture>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PillHero;
